'use client';
import { CheckIcon } from '@heroicons/react/20/solid';
import { ArrowUpRightIcon } from '@heroicons/react/24/outline';
import { useState, useEffect, ReactNode } from 'react';
import ModalForm from '../Modal/Modal';
import React from 'react';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import CustomRichText from '../CustomRichText';

type Feature = {
  id: string;
  name: ReactNode | SerializedEditorState;
  enters: boolean;
};

type Tier = {
  name: ReactNode | SerializedEditorState;
  id: string;
  priceSuffix: string;
  price: string;
  description: ReactNode | SerializedEditorState;
  features: Feature[];
  superPrice: boolean;
};

export interface PriceBlockUniversityProps {
  id?: string;
  title: ReactNode | SerializedEditorState;
  subtitle?: ReactNode | SerializedEditorState;
  selectPackageLabel?: string;
  showFeaturesLabel?: string;
  hideFeaturesLabel?: string;
  modalTagPrefix?: string;
  useDefaultTires?: boolean;
  tiers?: Tier[];
}

const defaultTiers: Tier[] = [
  {
    name: 'Basic Year',
    id: 'tier-0',
    priceSuffix: 'грн/рік',
    price: '24780',
    description: '',
    features: [
      {
        id: 'tier-0-1',
        name: (
          <>
            <p>Базові предмети:</p>
            <ul className=" list-inside list-disc">
              <li>Політологія</li>
              <li>Англійська мова</li>
              <li>Історія та соціологія</li>
              <li>Основи економіки</li>
              <li>Філософія та інші</li>
            </ul>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-0-2',
        name: (
          <>
            <p>
              За потреби підготовка до <strong>НМТ</strong>
            </p>
          </>
        ),
        enters: true,
      },
    ],
    superPrice: false,
  },
  {
    name: 'Bronze Year',
    id: 'tier-1',
    priceSuffix: 'грн/рік',
    price: '40887',
    description: '',
    features: [
      {
        id: 'tier-1-1',
        name: (
          <>
            <p>Англійська мова: теоретичні заняття (60 хв) — основи та практична граматика</p>
            <ul className="list-inside list-disc">
              <li>
                <strong>Стабільна мовна підготовка</strong> направлена на результат
              </li>
            </ul>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-1-2',
        name: (
          <>
            <p>
              <strong>Cупровід для вступу в іноземний університет:</strong> базова допомога з
              подачею документів
            </p>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-1-3',
        name: (
          <>
            <p>
              Факультативні заняття — <b>3</b> спеціалізовані курси:
            </p>
            <ul className=" list-inside list-disc">
              <li>Філософія свідомого вибору</li>
              <li>Компетенції успіху</li>
              <li>Технології інноваційного мислення</li>
            </ul>
          </>
        ),
        enters: true,
      },
    ],
    superPrice: false,
  },
  {
    name: 'silver Year',
    id: 'tier-2',
    priceSuffix: 'грн/рік',
    price: '59885',
    description: '',
    features: [
      {
        id: 'tier-2-1',
        name: (
          <>
            <p>Англійська мова: теоретичні заняття (60 хв) + практичні заняття (90 хв)</p>
            <ul className="list-inside list-disc">
              <li>
                <strong>Практична мовна підготовка</strong> для досягнення високого рівня володіння
                мовою{' '}
              </li>
            </ul>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-2-2',
        name: (
          <>
            <p>
              <strong>Cупровід для вступу в іноземний університет: </strong>
              подача заяв та оформлення документів (переклади, візи)
            </p>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-2-3',
        name: (
          <>
            <p>
              Факультативні заняття — <span className={'font-bold'}>7</span> спеціалізованих курсів:
            </p>
            <ul className="list-inside list-disc">
              <li>Інтегральне самопізнання</li>
              <li>Філософія свідомого вибору</li>
              <li>Компетенції успіху</li>
              <li>Динаміка професійного розвитку</li>
              <li>Технології інноваційного мислення</li>
              <li>Міжнародна комунікація та культурна грамотність</li>
              <li>Фінансова архітектура</li>
            </ul>
          </>
        ),
        enters: true,
      },
    ],
    superPrice: false,
  },
  {
    name: 'Golden Year',
    id: 'tier-3',
    price: '94990',
    priceSuffix: 'грн/рік',
    description: '',
    features: [
      {
        id: 'tier-3-1',
        name: (
          <>
            <p>
              Англійська мова: теоретичні заняття (60 хв) + практичні заняття (90 хв) +
              індивідуальні заняття (60 хв)
            </p>
            <p>
              · <strong>Гарантія на вивчення іноземної мови</strong> — супровід до успішного
              результату
            </p>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-3-2',
        name: (
          <>
            <p>
              <strong>Повний супровід для вступу в іноземний університет</strong> (переклад
              документів, подача заяв, оформлення візи, пошук житла)
            </p>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-3-3',
        name: (
          <>
            <p>
              <strong>Лідери галузей:</strong> зустрічі з лідерами, бізнес-тренінги, подача заяв на
              гранти, отримання стипендій
            </p>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-3-4',
        name: (
          <>
            <p>
              Факультативні заняття — всі <span className={'font-bold'}>14</span> спеціалізованих
              курсів:
            </p>
            <ul className="list-inside list-disc">
              <li>Інтегральне самопізнання</li>
              <li>Філософія свідомого вибору</li>
              <li>Академічна навігація</li>
              <li>Компетенції успіху</li>
              <li>Принципи незалежності</li>
              <li>Динаміка професійного розвитку</li>
              <li>Технології інноваційного мислення</li>
              <li>Міжнародна комунікація та культурна грамотність</li>
              <li>Психологія лідерства та успіху</li>
              <li>Історія людського прогресу</li>
              <li>Фінансова архітектура</li>
              <li>Економіка особистого добробуту</li>
              <li>Фінансове планування та інвестиційні стратегії</li>
              <li>Глобальна фінансова система</li>
            </ul>
          </>
        ),
        enters: true,
      },
      {
        id: 'tier-3-5',
        name: (
          <>
            <p>
              <strong>Індивідуальний план розвитку</strong> та повна підтримка викладачів
            </p>
          </>
        ),
        enters: true,
      },
    ],
    superPrice: false,
  },
];

export default function PriceBlockUniversity({
  id = 'price',
  title = 'Вартість навчання в університеті',
  subtitle = 'Оберіть пакет, який найкраще відповідає вашим потребам',
  selectPackageLabel = 'Обрати пакет',
  showFeaturesLabel = 'Показати більше',
  hideFeaturesLabel = 'Сховати',
  modalTagPrefix = 'університет',
  useDefaultTires = true,
  tiers = defaultTiers,
}: PriceBlockUniversityProps) {
  const [dispayedTiers] = useState<Tier[]>(useDefaultTires ? defaultTiers : tiers);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<Tier | null>(null);
  const [showAllFeatures, setShowAllFeatures] = useState(false);

  const openForm = () => setIsFormOpen(true);
  const closeForm = () => setIsFormOpen(false);

  const openModal = (tier: Tier) => {
    setSelectedTier(tier);
    setIsFormOpen(true);
  };

  const toggleFeatures = () => {
    setShowAllFeatures(prevShowAllFeatures => !prevShowAllFeatures);
  };

  useEffect(() => {
    const hashId = window.document.location.hash.slice(1);
    if (id === hashId) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [id]);

  return (
    <>
      <div id={id} data-meta="PriceBlockUniversity" className="py-14">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <CustomRichText
              data={title}
              tag="div"
              className="mt-2 text-4xl font-bold tracking-tight text-white sm:text-5xl"
            />
          </div>
          {subtitle && (
            <CustomRichText
              data={subtitle}
              tag="div"
              className="mx-auto mt-6 max-w-7xl text-center text-lg leading-8 text-white"
            />
          )}
          <div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-4 lg:mx-0 lg:max-w-none lg:grid-cols-4">
            {dispayedTiers.map(tier => (
              <div
                key={tier.id}
                className={`rounded-3xl p-4 bg-white flex flex-col justify-between ${
                  tier.superPrice ? 'ring-2 ring-indigo-600' : 'ring-1 ring-gray-200'
                }`}
              >
                <div>
                  <div className="flex items-center justify-between gap-x-4">
                    <CustomRichText
                      id={tier.id}
                      data={tier.name}
                      tag="div"
                      className="uppercase text-white rounded-full py-1 px-4 custom-gradient-blue text-base font-semibold"
                    />
                  </div>
                  <p className="mt-6 flex items-baseline gap-x-1 border-b-2 pb-4 border-blue-200">
                    <span className="text-4xl font-bold tracking-tight text-gray-900">
                      {tier.price}
                    </span>
                    <span className="text-sm font-semibold leading-6 text-gray-900">
                      {tier.priceSuffix}
                    </span>
                  </p>
                  <span className="flex items-baseline gap-x-1 text-xs text-gray-400">
                    <CustomRichText data={tier.description} tag="span" />
                  </span>
                  <button
                    aria-describedby={tier.id}
                    onClick={() => openModal(tier)}
                    className={` relative uppercase mt-6 flex items-center justify-center w-full ring-1 ring-inset ring-blue-500 rounded-full px-1 py-2 text-sm font-semibold leading-6 ${
                      tier.superPrice ? 'bg-gray-900 text-white' : 'text-gray-900'
                    }`}
                  >
                    <span>{selectPackageLabel}</span>
                    <div className="absolute right-1 flex items-center">
                      <div className="rounded-full custom-gradient-blue-btn">
                        <ArrowUpRightIcon className="text-white h-6 w-6 m-1" />
                      </div>
                    </div>
                  </button>
                </div>
                <ul className="mt-8 space-y-3 text-sm leading-6 text-gray-600 xl:mt-10 transition-all">
                  {(showAllFeatures ? tier.features : tier.features.slice(0, 2)).map(feature => (
                    <li key={feature.id} className="flex gap-x-3">
                      <CheckIcon className="h-6 w-6 flex-none text-white custom-gradient-blue-btn rounded-full" />
                      <CustomRichText data={feature.name} tag="span" className="" />
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => toggleFeatures()}
                  className="mt-auto pt-4 text-blue-600 font-bold text-sm "
                >
                  {showAllFeatures ? hideFeaturesLabel : showFeaturesLabel}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      {isFormOpen && selectedTier && (
        <ModalForm
          closeForm={closeForm}
          btnMeta={{tag: `${modalTagPrefix}-${selectedTier.name}`}}
          color={'blue'}
        />
      )}
    </>
  );
}
